<template>
    <div
        class="search-filter"
        @keydown.up.prevent="prev"
        @keydown.down.prevent="next"
        @keydown.esc.stop="close"
        v-esc="close"
    >
        <label :for="`searchFilter-${_uid}`" class="label" v-if="label" v-text="label" />
        <input
            type="text"
            class="input search-filter__input"
            autocomplete="off"
            :value="value"
            @input="$emit('input', $event.target.value)"
            @keyup="search"
            ref="input"
            :id="`searchFilter-${_uid}`"
            :placeholder="placeholder"
            aria-autocomplete="both"
            :aria-describedby="`searchFilterInstructions-${_uid}`"
            :aria-expanded="results.length ? 'true' : 'false'"
            :aria-owns="`searchFilterResults-${_uid}`"
            :disabled="disabled"
        >
        <ul
            class="search-filter__results"
            v-if="results.length"
            :id="`searchFilterResults-${_uid}`"
            tabindex="0"
            aria-label="Results list"
            ref="results"
        >
            <li
                v-for="(result, key) in results"
                :key="key"
                :class="['search-filter__result', { '-is-active': isActive(key) }]"
                role="option"
                :aria-selected="isActive(key) ? 'true' : 'false'"
            >
                <span
                    :ref="`result-${key}`"
                    :aria-label="result.title"
                    v-html="result.formatted"
                    @click="handleSelection(result)"
                    @keyup.enter="handleSelection(result)"
                    tabindex="0"
                />
            </li>
        </ul>
        <span class="sr-only" :id="`searchFilterInstructions-${_uid}`">
            When autocomplete results are available use up and down arrows to review and enter to select.
        </span>
        <span aria-live="assertive" class="sr-only" aria-atomic="true">There are {{ results.length }} results.</span>
    </div>
</template>

<script>
import $mediator from 'wee-mediator';
import { mapActions } from 'vuex';
import Vue from 'vue';
import VueEsc from 'vue-esc';
import debounce from 'lodash.debounce';
import KeyHelper from '../../../scripts/keyhelper';
import client from '../../../scripts/swiftypeClient';
import Tabbable from '../../../scripts/tabbable';

Vue.use(VueEsc);

const formatResult = (query, result) => {
    const re = new RegExp('(' + query.split(' ').join('|') + ')', 'gi');

    return `${result.replace(re, '<b>$1</b>')}`;
}

export default {
    props: {
        label: {
            type: String,
        },
        type: {
            type: String,
        },
        placeholder: {
            type: String,
            default: '',
        },
        value: {
            type: String,
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        emit(value) {
            this.$emit('input', value);
            this.results = [];
            this.$refs.input.focus();
        },
        ...mapActions(['setFilter']),
        handleSelection(result) {
            this.emit(result.title);
        },
        close() {
            this.results = [];
            this.state.activeIndex = -1;
        },
        next() {
            if (this.state.activeIndex < this.results.length - 1) {
                this.state.activeIndex += 1;

                this.$refs[`result-${this.state.activeIndex}`][0].focus();
            }
        },
        prev() {
            if (this.state.activeIndex > 0) {
                this.state.activeIndex -= 1;
                this.$refs[`result-${this.state.activeIndex}`][0].focus();
            }
        },
        isActive(key) {
            return key === this.state.activeIndex;
        },
        search: debounce(function(e) {
            const filters = {
                all: [
                    { type: this.type },
                ],
            };

            if (Object.keys(this.filters).length) {
                // TODO: Revisit this strategy
                filters.all.push({ ...this.filters })
            }

            if ((KeyHelper.isLetter(e) || KeyHelper.isDelete(e)) && this.value.length >= 2) {
                client.search(this.value, {
                    filters,
                    page: {
                        size: 5
                    },
                    result_fields: {
                        title: { raw: {} }
                    }
                }).then(({ results }) => {
                    this.results = results.map(result => ({
                        title: result.getRaw('title'),
                        formatted: formatResult(this.value, result.getRaw('title')),
                    }));

                    this.$nextTick(() => {
                        this.tabbable = new Tabbable({
                            el: this.$refs.results,
                            callback: () => {
                                this.tabbable.$firstEl[0].focus();
                            },
                        });
                    });
                });
            } else if (this.value.length === 0) {
                this.results = [];
                this.tabbable = null;
            }
        }, 200),
    },
    data() {
        return {
            results: [],
            internalValue: '',
            state: {
                activeIndex: -1,
                tabbable: null,
            },
        };
    },
};
</script>
